import cx from "classnames";
import * as React from "react";
import TooltipWrapper from "../../TooltipWrapper";

import { FormFieldStates, InputProps } from "./exports";
import { createContext, Dispatch, JSX, SetStateAction } from "react";

export type QESTextInputProps = InputProps & React.InputHTMLAttributes<HTMLInputElement> & TextInputProps;

interface TextInputProps {
  optionsList?: JSX.Element;
}

interface QESTextInputContext {
  focused: boolean;
  setFocused: Dispatch<SetStateAction<boolean>>;
  value: string | number | readonly string[];
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const TextInputContext = createContext<QESTextInputContext>({
  focused: false,
  onChange: () => {},
  setFocused: () => {},
  value: ""
});

/**
 * A standard text field for easy reuse. It requires a label above, has an optional descriptive snippet below, and accepts all the same props as a native HTML input element.
 */
/* We call out the custom props here individually, so ...props is just the normal HTML input attributes */
export const QESTextInput: React.FC<React.PropsWithChildren<QESTextInputProps>> = (props) => {
  const [focused, setFocused] = React.useState(false);
  const [hasBeenFocused, setHasBeenFocused] = React.useState(false);
  const [fieldState, setFieldState] = React.useState(FormFieldStates.None);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const {
    containerClassName,
    descriptiveText,
    descriptiveTextClassname,
    disabled,
    errorText,
    hideLabel,
    inputAppend,
    isValid,
    label,
    onlyAllowNumbers,
    optionsList,
    qesRef,
    required,
    testId,
    unrequiredValidation,
    validationTooltipContent,
    wasFocusedExternally,
    wasVisited,
    ...restProps
  } = props;

  React.useEffect(() => {
    if (!isValid && (required || unrequiredValidation) && (hasBeenFocused || focused)) {
      setFieldState(FormFieldStates.Error);
    } else {
      setFieldState(FormFieldStates.None);
    }
  }, [isValid, hasBeenFocused, focused, required, unrequiredValidation]);
  
  React.useEffect(() => {
    if (wasVisited) {
      setHasBeenFocused(wasVisited);
    }
  }, [wasVisited]);

  React.useEffect(() => {
    if (wasFocusedExternally) {
      setHasBeenFocused(wasFocusedExternally);
    }

    setFocused(wasFocusedExternally);
  }, [wasFocusedExternally]);

  const subText = React.useMemo(() => {
    if (hasBeenFocused && errorText && fieldState === FormFieldStates.Error) {
      return errorText;
    } else if (descriptiveText) {
      return descriptiveText;
    }
    return "";
  }, [descriptiveText, errorText, fieldState, hasBeenFocused]);

  const handleOnBlur = () => {
    if (!hasBeenFocused) {
      setHasBeenFocused(true)
    }

    setFocused(false);
  }

  const characterCount = React.useMemo(() => {
    return (props.value as string)?.length || 0;
  }, [props.value]);

  return (
    <div className={`qes-text-input__container ${containerClassName || ""}`} key={testId} onBlur={() => handleOnBlur()} ref={containerRef}>
      <label
        htmlFor={props.id || testId}
        className={cx({
          "qes-text-input__label": true,
          "visually-hidden-label": hideLabel,
        })}
      >
        {label}
        {required ? <span className="text-danger"> *</span> : ""}
        {validationTooltipContent ? <TooltipWrapper containerClassName="ml-3" text={validationTooltipContent}><i className="fa fa-question-circle" /></TooltipWrapper> : ""}
      </label>
      <div
        className={cx({
          "disabled": disabled,
          "error": hasBeenFocused && fieldState === FormFieldStates.Error,
          "focus": focused,
          [props.className]: !!props.className,
          "qes-text-input__group": true,
          "qes-text-input__options-container": !!props.optionsList,
        })}
      >
        <input
          {...restProps}
          disabled={disabled}
          id={props.id || testId}
          type={onlyAllowNumbers ? 'number' : 'text'}
          className={cx({
            "error": hasBeenFocused && fieldState === FormFieldStates.Error,
            "qes-text-input__input": true,
          })}
          onFocus={() => setFocused(true)}
          ref={qesRef as React.RefObject<HTMLInputElement>}
          required={required}
          data-testid={testId}
        />

        {!!inputAppend && (
          <span className="qes-text-input__append">
            {inputAppend}
          </span>
        )}
        {!!optionsList &&
          <TextInputContext.Provider value={{ focused, onChange: props.onChange, setFocused, value: props.value }}>
            {optionsList}
          </TextInputContext.Provider>
        }
      </div>
      <div>
        {subText && <small className={`qes-text-input__descriptive-text${!!descriptiveTextClassname ? ` ${descriptiveTextClassname}` : ""}`}>{subText}</small>}
        {props.maxLength && (
          <small
            className={cx({
              "danger": characterCount >= props.maxLength,
              "qes-text-input__descriptive-text qes-text-input__character-count": true,
            })}
          >
            {(props.showCounter === true || props.showCounter === undefined) && (
              <>
                {(props.maxLength - characterCount).toLocaleString()} / {props.maxLength.toLocaleString()}
              </>
            )}
          </small>
        )}
      </div>
    </div>
  );
};
